import type { FunctionComponent } from "react";
import { useMemo, useState } from "react";

import { config } from "~/data";

import { usePricing, useTracking } from "./hooks";
import type { Props as PlanOptionProps } from "./plan-option";
import { Container, EPaperPlan, Footer, PlanContainer, PrintPlan, Title, Toggle } from "./styles";
import type { BillingCycle, Bundle, Plan, Variant } from "./types";

type Props = {
  className?: string;
  variant: Variant;
};
export const SubscriptionWidget: FunctionComponent<Props> = ({ className, variant }) => {
  const { trackBillingCycleToggle, trackPlanSelected } = useTracking(variant);

  const [selectedBillingCycle, setSelectedBillingCycle] = useState<BillingCycle>("annual");
  const [selectedBundle, setSelectedBundle] = useState<Bundle>("print");
  const { pricing } = usePricing();
  const { plans, selectedPlan } = useMemo(() => {
    if (!pricing) return {};
    const bundles: Bundle[] = ["epaper", "print"];
    const plans = bundles.reduce((plans, bundle) => {
      plans[bundle] = {
        billingCycle: selectedBillingCycle,
        bundle,
        pricing: pricing[bundle][selectedBillingCycle],
      };
      return plans;
    }, {} as Record<Bundle, Plan>);

    return { plans, selectedPlan: plans[selectedBundle] };
  }, [selectedBillingCycle, selectedBundle, pricing]);
  if (!plans) return null;

  const computeViewModelVariant = () => {
    switch (variant) {
      case "brochure":
        return {
          handleBundleSelected: setSelectedBundle,
          hideSubscribeButton: false,
          withBalloon: true,
        };
      case "paywall":
        return {
          hideSubscribeButton: true,
          withBalloon: false,
          withLink: true,
        };
    }
  };
  const viewModelVariant = computeViewModelVariant();

  const computeSubscribeLink = (plan: Plan) => {
    // cspell: ignore postiesbundle
    const { billingCycle, bundle } = plan;
    const subscribeHost = config.rosetta.subscribeHost;
    const bundleName = bundle === "epaper" ? "posties" : "postiesbundle";
    const cycleName = billingCycle === "monthly" ? "1m" : "1y";
    return `${subscribeHost}/posties?plan=${bundleName}${cycleName}`;
  };
  const computePlanOptionProps = (plan: Plan) => {
    const props: PlanOptionProps = {
      handleSelected() {
        trackPlanSelected(plan);
        viewModelVariant.handleBundleSelected?.(plan.bundle);
      },
      isSelected: plan === selectedPlan,
      plan,
      withBalloon: viewModelVariant.withBalloon,
      withLink: viewModelVariant.withLink
        ? {
            url: computeSubscribeLink(plan),
          }
        : undefined,
    };
    return props;
  };

  return (
    <Container className={className}>
      <Title>Pick a plan that suits you</Title>
      <Toggle
        billingCycle={selectedBillingCycle}
        handleChange={value => {
          trackBillingCycleToggle(value);
          setSelectedBillingCycle(value);
        }}
      />
      <PlanContainer>
        <EPaperPlan {...computePlanOptionProps(plans.epaper)} />
        <PrintPlan {...computePlanOptionProps(plans.print)} />
      </PlanContainer>
      <Footer
        disableSubscribeButton={selectedPlan.pricing.isDisabled}
        hideSubscribeButton={viewModelVariant.hideSubscribeButton}
        subscribeUrl={computeSubscribeLink(selectedPlan)}
        variant={variant}
      />
    </Container>
  );
};

SubscriptionWidget.displayName = "SubscriptionWidget";
